import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import { IRepresentativeTruckImplement, defaultValue } from 'app/shared/model/representative-truck-implement.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<IRepresentativeTruckImplement> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/representative-truck-implements';

// Create a cancel token for the request
let cancelTokenSource = axios.CancelToken.source();

// Actions

export const getEntities = createAsyncThunk('representativeTruckImplement/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IRepresentativeTruckImplement[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'representativeTruckImplement/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IRepresentativeTruckImplement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'representativeTruckImplement/create_entity',
  async (entity: IRepresentativeTruckImplement, thunkAPI) => {
    const result = await axios.post<IRepresentativeTruckImplement>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'representativeTruckImplement/update_entity',
  async (entity: IRepresentativeTruckImplement, thunkAPI) => {
    const result = await axios.put<IRepresentativeTruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'representativeTruckImplement/partial_update_entity',
  async (entity: IRepresentativeTruckImplement, thunkAPI) => {
    const result = await axios.patch<IRepresentativeTruckImplement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'representativeTruckImplement/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IRepresentativeTruckImplement>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getRepresentativeTruckImplementsByCompany = createAsyncThunk(
  'representativeTruckImplement/fetch_by_company',
  async (companyId: string | number) => {
    const requestUrl = `${apiUrl}/company/${companyId}`;
    return axios.get<number[]>(requestUrl);
  }
);

interface IQueryParamsSearch extends IQueryParams {
  searchRepresentative?: string;
  searchLicensePlate?: string;
  searchCompanyName?: string;
  searchDescription?: string;
  loading?: boolean;
  status?: StatusFilter | string;
}

export const getRepresentativeTruckImplementByCompanyAndSearch = createAsyncThunk(
  'representativeTruckImplement/fetch_by_company_and_search',
  async ({ searchRepresentative, searchLicensePlate, searchCompanyName, loading, status, page, size, sort }: IQueryParamsSearch) => {
    const representative = `&searchRepresentative=${encodeURIComponent(searchRepresentative)}`;
    const licensePlate = `&searchLicensePlate=${encodeURIComponent(searchLicensePlate)}`;
    const company = `&searchCompanyName=${encodeURIComponent(searchCompanyName)}`;

    const requestUrl = `${apiUrl}/company/search?page=${page}&size=${size}&sort=${sort}&status=${status}${representative}${licensePlate}${company}&cacheBuster=${new Date().getTime()}`;

    if (cancelTokenSource && loading) {
      cancelTokenSource.cancel('Request canceled, because a new request was made.');
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      return await axios.get<IRepresentativeTruckImplement[]>(requestUrl, { cancelToken: cancelTokenSource.token });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('Canceled request: ', error.message);
      } else {
        throw error;
      }
    }
  }
);

// slice

export const RepresentativeTruckImplementSlice = createEntitySlice({
  name: 'representativeTruckImplement',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getRepresentativeTruckImplementsByCompany), (state, action) => {
        state.loading = false;
        state.entitiesAlt = action.payload.data;
      })
      .addMatcher(isFulfilled(getRepresentativeTruckImplementByCompanyAndSearch), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getRepresentativeTruckImplementsByCompany, getRepresentativeTruckImplementByCompanyAndSearch, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = RepresentativeTruckImplementSlice.actions;

// Reducer
export default RepresentativeTruckImplementSlice.reducer;
